/**
 * Search box for a paginated [answer_post_list] (markup: Answer_Posts::answer_post_list_sc()).
 *
 * The page only holds one page of history, so the search runs on the server over the game's whole
 * history (tryhardguides/v1/answer-history) and the matching rows replace the list. Clearing the box
 * puts the page's own list and pagination back, from memory rather than a new request.
 */
const MIN_LENGTH = 2;
const DEBOUNCE_MS = 300;

function initSearch( root ) {

    const input  = root.querySelector( '.answer-history-search-input' );
    const status = root.querySelector( '.answer-history-search-status' );
    const list   = root.querySelector( '.answer-history-list' );

    if ( ! input || ! list ) {
        return;
    }

    // Shown only while not searching: the page's range line and its pagination.
    const pageOnly = [
        root.querySelector( '.answer-history-range' ),
        root.querySelector( 'nav[aria-label="Answer history pages"]' ),
    ].filter( Boolean );

    const originalRows = list.innerHTML;

    let timer      = null;
    let controller = null;

    function setStatus( text ) {
        status.textContent = text;
        status.hidden = ! text;
    }

    function restore() {
        if ( controller ) {
            controller.abort();
        }

        list.innerHTML = originalRows;
        pageOnly.forEach( ( el ) => { el.hidden = false; } );
        setStatus( '' );
    }

    async function search( query ) {

        if ( controller ) {
            controller.abort();
        }

        controller = new AbortController();

        const url = new URL( root.dataset.answerHistorySearch );

        url.searchParams.set( 'tag', root.dataset.tag );
        url.searchParams.set( 'offset', root.dataset.offset );
        url.searchParams.set( 'show_number', root.dataset.showNumber );
        url.searchParams.set( 'show_date', root.dataset.showDate );
        url.searchParams.set( 'list', root.dataset.list );
        url.searchParams.set( 'q', query );

        setStatus( 'Searching…' );

        let data;

        try {
            const response = await fetch( url, { signal: controller.signal } );

            if ( ! response.ok ) {
                throw new Error( `HTTP ${ response.status }` );
            }

            data = await response.json();
        } catch ( e ) {
            if ( e.name !== 'AbortError' ) {
                setStatus( 'Search is unavailable right now.' );
            }
            return;
        }

        pageOnly.forEach( ( el ) => { el.hidden = true; } );
        list.innerHTML = data.html;

        if ( ! data.total_matches ) {
            setStatus( 'No results found.' );
        } else if ( data.truncated ) {
            setStatus( `Showing the ${ data.count } newest of ${ data.total_matches } matches.` );
        } else {
            setStatus( `${ data.total_matches } ${ data.total_matches === 1 ? 'match' : 'matches' }.` );
        }
    }

    input.addEventListener( 'input', () => {

        clearTimeout( timer );

        const query = input.value.trim();

        if ( ! query ) {
            restore();
            return;
        }

        if ( query.length < MIN_LENGTH ) {
            return;
        }

        timer = setTimeout( () => search( query ), DEBOUNCE_MS );
    } );

    // Enter would otherwise submit nothing useful; search at once instead of waiting out the debounce.
    input.addEventListener( 'keydown', ( e ) => {
        if ( e.key === 'Enter' ) {
            e.preventDefault();
            clearTimeout( timer );
            const query = input.value.trim();
            if ( query.length >= MIN_LENGTH ) {
                search( query );
            }
        }
    } );
}

document.querySelectorAll( '[data-answer-history-search]' ).forEach( initSearch );
